import { getMarketColor } from '@talos/kyoko';
import type { DefaultTheme } from 'styled-components';

/**
 * Distinct fallback palette for exchanges/assets that have no brand color. Two hue bands are
 * deliberately excluded to avoid colliding with lines that DO have a color:
 *  - blue — reserved for the price overlay line;
 *  - green/teal — the most crowded band in the exchange brand map (bitfinex, huobi, kucoin,
 *    bitstamp, deribit…), so a green fallback reads as one of those brands.
 */
function getFundingFallbackPalette(theme: DefaultTheme): string[] {
  return [
    theme.colors.purple.default,
    theme.colors.orange.default,
    theme.colors.red.default,
    theme.colors.yellow.default,
    theme.colors.purple.lighten,
    theme.colors.orange.lighten,
    theme.colors.red.lighten,
    theme.colors.yellow.lighten,
  ];
}

/**
 * Deterministic 32-bit string hash (djb2). The same key always maps to the same palette index, so a
 * fallback-colored exchange keeps its color regardless of which other exchanges are selected —
 * unlike an index-into-array scheme, where removing one line recolors the others.
 */
function hashString(value: string): number {
  let hash = 5381;
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/** Deterministic non-blue palette color for any key (exchange name or asset id). */
export function getFallbackSeriesColor(key: string, theme: DefaultTheme): string {
  const palette = getFundingFallbackPalette(theme);
  return palette[hashString(key) % palette.length];
}

/**
 * Resolves a stable line color for an exchange: its brand color when Talos knows one, otherwise a
 * deterministic palette color derived from the name.
 *
 * `getMarketColor` returns `theme.colors.blue.lighten` for exchanges with no brand color — and that
 * blue is the price-line color, so those unmapped exchanges are re-mapped to the palette to avoid
 * every unknown venue (and the price line) collapsing onto the same blue.
 */
export function getFundingExchangeColor(exchange: string, theme: DefaultTheme): string {
  const brand = getMarketColor(exchange, theme);
  if (brand !== theme.colors.blue.lighten) {
    return brand;
  }
  return getFallbackSeriesColor(exchange, theme);
}
